<template>
    <div class="swf-anim" ref="containerRef"></div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from "vue";
import { Pet } from "../core/pet";
import {
    PhysicalAttackSkill,
    SpecialAttackSkill,
    StatusSkill,
    type Skill,
} from "../core/skill";
import { LruPool } from "../core/lru";

const props = defineProps<{
    pet: Pet;
    transform: boolean;
}>();

const emit = defineEmits<{
    (e: "complete", label: string): void;
}>();

/** 每侧常驻 Ruffle 播放器上限（最近使用池） */
const MAX_POOL_SIZE = 3;

/** 技能类 → swf 标签名 */
function skillLabel(skill: Skill): string {
    if (skill instanceof SpecialAttackSkill) return "sa";
    if (skill instanceof StatusSkill) return "cp";
    if (skill instanceof PhysicalAttackSkill) return "attack";
    return "attack";
}

/** 各标签的默认播放时长（ms） */
const DURATION: Record<string, number> = {
    attack: 2000,
    sa: 1300,
    cp: 1300,
};

const containerRef = ref<HTMLElement | null>(null);

interface PoolEntry {
    player: RufflePlayerInstance;
    url: string;
}

/** 皮肤 id → 播放器，LRU 淘汰；Map 迭代顺序即最近使用顺序 */
const pool = new LruPool<number, PoolEntry>(MAX_POOL_SIZE);
/** 当前应展示的皮肤 id（加载完成后按此判断是否展示） */
let activeSkinId: number | null = null;
/** 当前实际显示中的皮肤 id（切换动画加载完成前，旧精灵保持显示） */
let shownSkinId: number | null = null;
/** 递增序号，防止旧加载完成后覆盖新切换结果 */
let loadSeq = 0;
let disposed = false;

function delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

function destroyPlayer(entry: PoolEntry) {
    entry.player.pause();
    entry.player.remove();
}

function showAndResume(entry: PoolEntry) {
    entry.player.classList.remove("swf-hidden");
    if (!entry.player.isPlaying) {
        entry.player.play();
    }
}

function hidePlayer(skinId: number) {
    const entry = pool.peek(skinId);
    if (!entry) return;
    entry.player.classList.add("swf-hidden");
    if (entry.player.isPlaying) {
        entry.player.pause();
    }
}

/**
 * 激活指定皮肤的播放器：
 * - 已常驻：直接显示并恢复播放（零延迟切换）
 * - 未常驻：创建播放器并加载；容量满时淘汰最久未使用的非激活播放器
 */
function activateSkin(skinId: number) {
    activeSkinId = skinId;

    const existing = pool.get(skinId);
    if (existing) {
        if (shownSkinId !== null && shownSkinId !== skinId) {
            hidePlayer(shownSkinId);
        }
        showAndResume(existing);
        shownSkinId = skinId;
        return;
    }

    const ruffle = window.RufflePlayer?.newest();
    const container = containerRef.value;
    if (!ruffle || !container) {
        console.warn("[swf] Ruffle 未加载，无法播放精灵动画");
        return;
    }

    const player = ruffle.createPlayer();
    if (props.transform) {
        player.classList.add("transform");
    }
    player.classList.add("swf-hidden");
    container.appendChild(player);

    const url = props.pet.getBattleAnimUrl();
    const token = ++loadSeq;
    pool.set(skinId, { player, url });

    // 容量满则淘汰最久未使用的播放器（保护新激活皮肤与仍在显示的旧皮肤），保证常驻总数封顶
    for (const [, entry] of pool.trim(skinId, shownSkinId ?? skinId)) {
        destroyPlayer(entry);
    }

    console.debug(`[swf] load: ${url}`);
    void player.load(url).then(
        () => {
            if (disposed || !player.isConnected) return;
            if (activeSkinId === skinId && token === loadSeq) {
                if (shownSkinId !== null && shownSkinId !== skinId) {
                    hidePlayer(shownSkinId);
                }
                showAndResume({ player, url });
                shownSkinId = skinId;
            } else if (player.isPlaying) {
                // 后台预载完成的隐藏播放器：暂停避免空转
                player.pause();
            }
        },
        (err) => {
            if (!disposed && player.isConnected) {
                console.warn(`[swf] 加载失败: ${url}`, err);
            }
            pool.delete(skinId);
            player.remove();
        }
    );
}

onMounted(() => {
    activateSkin(props.pet.当前皮肤.id);
});

// 精灵切换或皮肤切换时激活对应皮肤的播放器
watch(
    () => props.pet.当前皮肤.id,
    (id) => activateSkin(id)
);

onBeforeUnmount(() => {
    disposed = true;
    for (const [, entry] of pool.entries()) {
        destroyPlayer(entry);
    }
    pool.clear();
});

defineExpose({
    /**
     * 播放出招动画
     * @param skill 技能
     */
    playAttack: async (skill: Skill) => {
        const label = skillLabel(skill);
        await delay(DURATION[label] ?? 1500);
        emit("complete", label);
    },
    /** 播放待机动画 */
    playIdle: () => {},
});
</script>

<style scoped>
.swf-anim {
    position: relative;
    width: 100%;
    height: 100%;
}

.swf-anim :deep(embed),
.swf-anim :deep(ruffle-player),
.swf-anim :deep(ruffle-embed) {
    width: 100%;
    height: 100%;
    pointer-events: none;
}

.swf-anim :deep(embed).transform,
.swf-anim :deep(ruffle-player).transform,
.swf-anim :deep(ruffle-embed).transform {
    transform: scaleX(-1);
}

/* 非当前展示的常驻播放器：保留布局尺寸但不可见 */
.swf-anim :deep(ruffle-player).swf-hidden,
.swf-anim :deep(ruffle-embed).swf-hidden {
    visibility: hidden;
    position: absolute;
    inset: 0;
}
</style>
